import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { RootLayout } from "@/components/layout/RootLayout";
import { useAuth } from "@/context/AuthContext";
import { Helmet } from "react-helmet";

/**
 * Renders the Terms of Service page for TellerWand.
 * @example
 * Terms()
 * <div>...</div>
 * @returns {JSX.Element} A JSX Element containing the Terms of Service content and layout.
 * @description
 *   - Integrates multiple UI components: Logo, Button, Link, ArrowLeft, Card, CardContent, and UnifiedFooter.
 *   - Provides navigation options for login and signing up, as well as a back button to the home page.
 *   - Contains detailed sections of the Terms of Service, including user responsibilities, subscription plans, and indemnification.
 *   - Utilizes Tailwind CSS for styling purposes, ensuring responsive design adjustments.
 */
export default function Terms() {
  const { user } = useAuth();

  const termsContent = (
    <Card className="mb-6">
      <CardContent className="prose dark:prose-invert max-w-none p-3 sm:p-4 md:p-6">
        <p className="text-sm sm:text-base md:text-lg font-medium">Last updated: January 5, 2026</p>

        <p className="mt-4">
          Welcome to TellerWand. Please read these Terms of Service ("Terms") carefully before using
          the TellerWand website, mobile applications, and services (collectively, the "Service")
          operated by TellerWand Inc. ("TellerWand," "we," "us," or "our").
        </p>

        <p>
          By accessing or using our Service, you agree to be bound by these Terms. If you disagree
          with any part of the Terms, then you may not access the Service.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">1. Acceptance of Terms</h2>
        <p>
          By creating an account or accessing or using the TellerWand Service, you hereby accept and
          agree to be bound by these Terms, our Privacy Policy, and any other legal notices,
          policies, or guidelines that we make available to you in connection with the Service. If
          you do not agree with these Terms, you must not use our Service.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">2. Description of Service</h2>
        <p>
          TellerWand provides a personal financial management platform that allows users to track
          assets, monitor investments, analyze expenses, and set financial goals. The Service may
          include:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Asset tracking and management tools</li>
          <li>Investment portfolio analysis</li>
          <li>Expense and budget monitoring</li>
          <li>Financial goal planning and tracking</li>
          <li>Reports and insights about your financial status</li>
          <li>Family financial management (Premium feature)</li>
          <li>Financial calculators (Income Tax Calculator, etc.)</li>
          <li>SplitMage - Expense splitting feature</li>
          <li>Referral program</li>
          <li>Financial education blog and content</li>
          <li>Other related financial management features</li>
        </ul>
        <p>
          TellerWand reserves the right to modify, suspend, discontinue, add, or introduce new
          services or any part of the Service at any time, with or without notice.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">3. User Accounts</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.1 Account Requirement</h3>
        <p>
          <strong>
            To use any features of the Service, including current and future offerings, you must
            create an account and log in.
          </strong>{" "}
          You agree to:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Provide accurate, current, and complete information during registration</li>
          <li>
            Maintain and update your account information to keep it accurate, current, and complete
          </li>
          <li>Maintain the security of your password and account</li>
          <li>
            Accept responsibility for all activities that occur under your account, whether
            authorized or not
          </li>
          <li>
            Notify us immediately of any unauthorized use of your account or any other breach of
            security
          </li>
          <li>Use only one account per person (multiple accounts are prohibited)</li>
          <li>Not share your account credentials with any third party</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.2 Account Eligibility</h3>
        <p>
          <strong>Age Requirement:</strong> You must be at least 18 years of age to use the Service.
          By using the Service, you represent and warrant that you are at least 18 years old and
          have the legal capacity to enter into these Terms.
        </p>
        <p>
          <strong>Minors:</strong> If you are under 18 years of age, you may not use the Service. If
          you are a parent or legal guardian and believe your minor child has created an account
          without your permission, please contact us immediately at support@tellerwand.com. We will
          take steps to verify the account and may suspend or terminate the account if it is
          determined to be created by a minor without proper authorization.
        </p>
        <p>
          <strong>Parental Consent:</strong> If you are between 13 and 17 years of age, you may only
          use the Service with the express consent and supervision of a parent or legal guardian who
          agrees to be bound by these Terms on your behalf. The parent or guardian is responsible
          for all activities conducted through the account.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">3.3 Account Security</h3>
        <p>
          You are solely responsible for maintaining the confidentiality of your account credentials
          and for all activities that occur under your account. You agree not to share your account
          information with any third party. TellerWand is not liable for any loss or damage arising
          from your failure to protect your account information or from unauthorized access to your
          account due to your negligence.
        </p>
        <p>
          You must immediately notify TellerWand of any unauthorized use of your account or any
          other breach of security. TellerWand will not be liable for any loss or damage arising
          from your failure to comply with this requirement.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">4. Subscription Plans and Pricing</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.1 Available Plans</h3>
        <p>TellerWand offers the following subscription plans:</p>
        <p>
          <strong>Free Plan:</strong>
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Price: ₹0/month</li>
          <li>
            Features: Basic asset tracking, expense monitoring, goal setting, limited reports, up to
            10 accounts
          </li>
        </ul>
        <p>
          <strong>Monthly Pro Plan:</strong>
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Price: ₹99/month</li>
          <li>Billing: Monthly recurring</li>
          <li>
            Features: Unlimited accounts, unlimited reports, advanced analytics, financial advisor
            features, family management
          </li>
        </ul>
        <p>
          <strong>Annual Pro Plan:</strong>
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Price: ₹949/year (equivalent to ₹79.08/month)</li>
          <li>Billing: Annual, paid upfront</li>
          <li>Discount: 20% savings compared to monthly plan</li>
          <li>Features: All Pro features, same as Monthly Pro</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.2 Pricing and Payment</h3>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>All prices are in Indian Rupees (INR) and are exclusive of applicable taxes</li>
          <li>Payments are processed through secure third-party payment gateways</li>
          <li>Subscription fees are charged in advance for the billing period</li>
          <li>You authorize TellerWand to charge your payment method for all subscription fees</li>
          <li>
            All payments are non-refundable except as expressly stated in our Refunds &
            Cancellations Policy
          </li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.3 Subscription Changes</h3>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You may upgrade your plan at any time</li>
          <li>Upgrades take effect immediately</li>
          <li>Downgrades take effect at the end of your current billing period</li>
          <li>No refunds are provided for downgrades during an active billing period</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">4.4 Subscription Renewal</h3>
        <p>
          <strong>Manual Renewal Required:</strong> Subscriptions do NOT automatically renew. You
          must manually renew your subscription before the expiration date to continue accessing
          Premium features.
        </p>
        <p>
          <strong>Expiration and Conversion to Free Plan:</strong>
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            If you do not manually renew your subscription before the expiration date, your
            subscription will automatically convert to the Free Plan
          </li>
          <li>
            Upon conversion to Free Plan, you will immediately lose access to all Premium features
            and content
          </li>
          <li>
            Any Premium data or features will become inaccessible until you renew your subscription
          </li>
          <li>
            TellerWand is not responsible for any data loss or feature unavailability resulting from
            subscription expiration
          </li>
        </ul>
        <p>
          <strong>Renewal Reminders:</strong> TellerWand may send renewal reminders via email, but
          you are solely responsible for ensuring timely renewal. Failure to receive reminders does
          not excuse non-renewal.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">5. User Responsibilities</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">5.1 Acceptable Use</h3>
        <p>
          You agree to use the Service only for lawful purposes and in accordance with these Terms.
          You agree not to:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Violate any applicable laws, regulations, or third-party rights</li>
          <li>Infringe upon the intellectual property, privacy, or other rights of others</li>
          <li>Transmit any harmful, offensive, defamatory, or illegal content</li>
          <li>
            Attempt to gain unauthorized access to the Service, other accounts, or TellerWand's
            systems
          </li>
          <li>
            Use automated systems (bots, scrapers, scripts) to access, monitor, or interact with the
            Service
          </li>
          <li>Interfere with or disrupt the Service, servers, networks, or security measures</li>
          <li>
            Reverse engineer, decompile, disassemble, or attempt to derive source code from the
            Service
          </li>
          <li>
            Use the Service to compete with TellerWand or build a similar or competing service
          </li>
          <li>
            Circumvent or attempt to circumvent any security measures, access controls, or usage
            limits
          </li>
          <li>Use the Service to transmit viruses, malware, or other harmful code</li>
          <li>Collect or harvest information about other users without their consent</li>
          <li>
            Impersonate any person or entity or falsely state or misrepresent your affiliation with
            any person or entity
          </li>
          <li>
            Use the Service in any manner that could damage, disable, overburden, or impair
            TellerWand's servers or networks
          </li>
          <li>Use the Service to violate any applicable export control laws or regulations</li>
          <li>Engage in any activity that violates these Terms or any applicable laws</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">
          5.2 Data Accuracy and User Responsibility
        </h3>
        <p>
          <strong>Your Sole Responsibility:</strong> You are solely and entirely responsible for the
          accuracy, completeness, and timeliness of all financial data, information, and content you
          enter, upload, or provide to the Service.
        </p>
        <p>
          <strong>No Liability for User Data:</strong> TellerWand is not responsible for, and
          disclaims all liability arising from:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Any errors, omissions, inaccuracies, or incompleteness in data you provide</li>
          <li>Financial decisions made based on inaccurate or incomplete data you entered</li>
          <li>Losses or damages resulting from reliance on incorrect data in the Service</li>
          <li>Discrepancies between your actual financial situation and data in the Service</li>
          <li>Failure to update or maintain accurate information in your account</li>
          <li>Data entry errors, calculation mistakes, or misclassifications made by you</li>
        </ul>
        <p>
          <strong>Data Verification:</strong> You acknowledge that:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You must independently verify all financial information before making decisions</li>
          <li>TellerWand does not verify, audit, or validate the accuracy of your data</li>
          <li>The Service relies entirely on the data you provide</li>
          <li>You should maintain independent records of your financial information</li>
          <li>You should consult with qualified professionals for financial advice</li>
        </ul>
        <p>
          <strong>No Financial Advice:</strong> TellerWand provides tools and information for
          financial management but does not provide financial, investment, tax, or legal advice. The
          Service is for informational and organizational purposes only. You should consult with
          qualified professionals (financial advisors, accountants, lawyers) for personalized
          financial advice.
        </p>
        <p>
          <strong>Third-Party Data:</strong> If the Service integrates with third-party financial
          institutions or data sources, TellerWand is not responsible for the accuracy,
          availability, or completeness of such third-party data.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">5.3 Financial Advice Disclaimer</h3>
        <p>
          TellerWand provides tools and information for financial management but does not provide
          financial, investment, tax, or legal advice. The Service is for informational purposes
          only. You should consult with qualified professionals for personalized financial advice.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">
          5.4 Prohibited Activities - Anti-Spam and Abuse
        </h3>
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            Send unsolicited emails, messages, or communications using the Service or TellerWand's
            infrastructure
          </li>
          <li>
            Use the Service to harvest email addresses, personal information, or contact details
          </li>
          <li>Engage in any form of spam, phishing, fraudulent activity, or social engineering</li>
          <li>
            Create multiple accounts to circumvent restrictions, abuse the Service, or manipulate
            referral programs
          </li>
          <li>
            Use automated systems, bots, scripts, or programs to create accounts, generate content,
            or interact with the Service
          </li>
          <li>
            Attempt to manipulate, game, or exploit referral programs, promotional offers, or reward
            systems
          </li>
          <li>Use the Service to distribute malware, viruses, or other harmful code</li>
          <li>
            Engage in any activity designed to overload, crash, or disrupt the Service or its
            infrastructure
          </li>
          <li>Attempt to bypass security measures, access controls, or usage limitations</li>
          <li>
            Use the Service to engage in any illegal activities or facilitate illegal transactions
          </li>
          <li>Create fake accounts, use false identities, or provide misleading information</li>
          <li>Coordinate with others to abuse, exploit, or manipulate the Service</li>
          <li>
            Use the Service to violate any applicable laws, regulations, or third-party rights
          </li>
          <li>
            Engage in any activity that could harm TellerWand's reputation, business, or users
          </li>
        </ul>
        <p>
          <strong>Consequences of Violation:</strong> Violation of these prohibitions may result in
          immediate account suspension or termination, forfeiture of all data and subscriptions,
          legal action, and reporting to appropriate authorities.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">6. Intellectual Property</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">6.1 Ownership</h3>
        <p>
          All content, features, and functionality of the Service, including but not limited to
          text, graphics, logos, icons, images, software, and code, are owned by TellerWand or its
          licensors and are protected by copyright, trademark, and other intellectual property laws.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">6.2 License to Use</h3>
        <p>
          TellerWand grants you a limited, non-exclusive, non-transferable, revocable license to
          access and use the Service for your personal, non-commercial use, subject to these Terms.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">6.3 User Content</h3>
        <p>
          You retain ownership of any data and content you upload to the Service. By uploading
          content, you grant TellerWand a worldwide, royalty-free, non-exclusive license to use,
          store, and process your content solely for the purpose of providing the Service to you.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">7. Privacy and Data Protection</h2>
        <p>
          Your privacy is important to us. Our collection and use of personal information is
          governed by our Privacy Policy, which is incorporated into these Terms by reference. By
          using the Service, you consent to the collection and use of your information as described
          in the Privacy Policy.
        </p>
        <p>
          TellerWand complies with applicable data protection regulations, including GDPR, CCPA, and
          Indian data protection laws.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">8. Refunds and Cancellations</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">8.1 Cancellation</h3>
        <p>
          You may cancel your subscription at any time by sending an email to support@tellerwand.com
          with your cancellation request. Cancellation takes effect at the end of your current
          billing period. You will continue to have access to Pro features until the end of the paid
          period.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">8.2 Refund Policy</h3>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            <strong>Monthly Subscriptions:</strong> No refunds are provided for monthly
            subscriptions. Cancellation stops future charges.
          </li>
          <li>
            <strong>Annual Subscriptions:</strong> Refunds for annual subscriptions are handled on a
            case-by-case basis. Contact support@tellerwand.com for refund requests.
          </li>
          <li>
            <strong>Free Plan:</strong> No payment required, no refund applicable.
          </li>
        </ul>
        <p>
          For detailed refund and cancellation policies, please refer to our Refunds & Cancellations
          Policy.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">
          9. Service Availability and Modifications
        </h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">9.1 Service Availability</h3>
        <p>
          TellerWand strives to maintain Service availability but does not guarantee uninterrupted
          or error-free operation. The Service may be temporarily unavailable due to maintenance,
          updates, or unforeseen circumstances.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">9.2 Service Modifications</h3>
        <p>TellerWand reserves the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            Modify, suspend, discontinue, add, or introduce new services or any part of the Service
          </li>
          <li>Change features, functionality, or pricing</li>
          <li>Impose limits on usage</li>
          <li>Remove or restrict access to content</li>
        </ul>
        <p>We will provide reasonable notice of material changes when possible.</p>

        <h3 className="text-lg font-semibold mt-4 mb-2">9.3 Force Majeure</h3>
        <p>
          TellerWand shall not be liable for any failure or delay in performance under these Terms
          which is due to circumstances beyond our reasonable control, including but not limited to:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Natural disasters, pandemics, or acts of God</li>
          <li>War, terrorism, or civil unrest</li>
          <li>Government actions or regulations</li>
          <li>Internet or telecommunications failures</li>
          <li>Third-party service provider failures</li>
          <li>Cyberattacks or security breaches beyond our control</li>
        </ul>
        <p>
          In such events, TellerWand will make reasonable efforts to restore service and will notify
          users of any significant disruptions.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">10. Termination</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">10.1 Termination by You</h3>
        <p>
          You may terminate your account at any time by sending an email to support@tellerwand.com
          requesting account termination. You must include your account email address and confirm
          your identity. Upon termination, your account and all associated data will be permanently
          deleted in accordance with our data retention policies.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">10.2 Termination by TellerWand</h3>
        <p>
          TellerWand may suspend or terminate your account immediately, without prior notice, if
          you:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Violate these Terms</li>
          <li>Engage in fraudulent, abusive, or illegal activity</li>
          <li>Fail to pay subscription fees</li>
          <li>Otherwise misuse the Service</li>
          <li>Engage in any activity that harms TellerWand or other users</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">10.3 Effect of Termination</h3>
        <p>Upon termination:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Your right to use the Service immediately ceases</li>
          <li>You may lose access to your data</li>
          <li>
            TellerWand may delete your account and data in accordance with our data retention
            policies
          </li>
          <li>Any outstanding fees remain due</li>
          <li>All licenses and rights granted to you under these Terms immediately terminate</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">11. Limitation of Liability</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.1 Disclaimer of Warranties</h3>
        <p className="uppercase font-semibold">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER
          EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.2 Limitation of Liability</h3>
        <p className="uppercase font-semibold">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, TELLERWAND SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS
          OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICE.
        </p>
        <p className="uppercase font-semibold">
          TELLERWAND'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID TO TELLERWAND IN THE
          TWELVE (12) MONTHS PRECEDING THE CLAIM.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.3 Financial Data</h3>
        <p>TellerWand is not responsible for:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Loss of financial data due to user error</li>
          <li>Inaccuracies in data entered by users</li>
          <li>Financial decisions made based on Service information</li>
          <li>Third-party service failures affecting data synchronization</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.4 Data Backup and Recovery</h3>
        <p>While TellerWand implements reasonable data backup and security measures:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You are responsible for maintaining your own backups of critical data</li>
          <li>TellerWand does not guarantee data recovery in all circumstances</li>
          <li>
            Data loss due to user error, system failures, or security breaches may not be
            recoverable
          </li>
          <li>TellerWand is not liable for data loss beyond the value of your subscription fees</li>
          <li>Regular data exports are recommended for important financial information</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">11.5 No Service Level Guarantees</h3>
        <p>TellerWand does not guarantee:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Specific uptime percentages or availability</li>
          <li>Response times for support requests</li>
          <li>Data processing speeds or system performance</li>
          <li>Accuracy of third-party data integrations</li>
          <li>Compatibility with all devices or browsers</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">12. Indemnification</h2>
        <p>
          You agree to indemnify, defend, and hold harmless TellerWand, its officers, directors,
          employees, agents, affiliates, licensors, and service providers from and against any and
          all claims, damages, obligations, losses, liabilities, costs, debts, expenses (including
          reasonable attorney's fees), and settlements arising from or related to:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Your use of the Service or any violation of these Terms</li>
          <li>
            Your violation of any rights of another party (including intellectual property, privacy,
            or publicity rights)
          </li>
          <li>Your violation of any applicable laws, rules, or regulations</li>
          <li>
            Any content, data, or information you submit, post, transmit, or make available through
            the Service
          </li>
          <li>Your fraudulent, negligent, or willful misconduct</li>
          <li>Any unauthorized use of your account, whether by you or a third party</li>
          <li>Any disputes between you and third parties related to your use of the Service</li>
          <li>
            Any claims that your use of the Service infringes or violates any third-party rights
          </li>
        </ul>
        <p>
          This indemnification obligation will survive termination of these Terms and your use of
          the Service. TellerWand reserves the right, at its own expense, to assume the exclusive
          defense and control of any matter otherwise subject to indemnification by you, in which
          event you will cooperate with TellerWand in asserting any available defenses.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">13. Dispute Resolution</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">13.1 Governing Law</h3>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of India,
          without regard to its conflict of law provisions.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">13.2 Dispute Resolution Process</h3>
        <p>In the event of any dispute arising out of or relating to these Terms or the Service:</p>
        <ol className="list-decimal pl-6 mt-2 mb-4 space-y-1">
          <li>First, contact our support team at support@tellerwand.com</li>
          <li>We will attempt to resolve the dispute through good faith negotiations</li>
          <li>
            If unresolved, disputes shall be subject to the exclusive jurisdiction of the courts in
            Bangalore, Karnataka, India
          </li>
        </ol>

        <h3 className="text-lg font-semibold mt-4 mb-2">13.3 Class Action Waiver</h3>
        <p>
          To the maximum extent permitted by law, you agree that any dispute resolution proceedings
          will be conducted only on an individual basis and not in a class, consolidated, or
          representative action.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">13.4 Time Limitation for Claims</h3>
        <p>
          Any claim or cause of action arising out of or related to the Service or these Terms must
          be filed within ninety (90) days after such claim or cause of action arose, or be forever
          barred. This limitation period is shorter than the standard limitation period to ensure
          timely resolution of disputes and to protect both parties' interests.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">14. Changes to Terms</h2>
        <p>
          TellerWand reserves the right to modify these Terms at any time. We will notify you of
          material changes by:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Posting the updated Terms on the Service</li>
          <li>Sending an email to your registered email address</li>
          <li>Displaying a notice on the Service</li>
        </ul>
        <p>
          Your continued use of the Service after changes become effective constitutes acceptance of
          the modified Terms. If you do not agree to the changes, you must stop using the Service
          and may cancel your account.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">15. Third-Party Services and Content</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">15.1 Third-Party Integrations</h3>
        <p>
          The Service may integrate with or link to third-party services, websites, or applications.
          TellerWand does not control, endorse, or assume responsibility for:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Third-party services, their content, or privacy practices</li>
          <li>Data accuracy from third-party sources</li>
          <li>Availability or functionality of third-party integrations</li>
          <li>Security of third-party services</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">15.2 Third-Party Content</h3>
        <p>
          The Service may display, include, or make available content, data, or services provided by
          third parties. Your use of third-party content is subject to their respective terms and
          conditions. TellerWand is not responsible for third-party content accuracy or
          availability.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">15.3 API Usage</h3>
        <p>If you access TellerWand's API or use third-party APIs through our Service:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You must comply with all applicable API terms and rate limits</li>
          <li>You are responsible for securing your API credentials</li>
          <li>TellerWand may revoke API access for violations</li>
          <li>API access is provided "as-is" without warranties</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">16. Beta Features and Preview Services</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">16.1 Beta Features</h3>
        <p>
          TellerWand may offer beta, preview, or experimental features ("Beta Features"). Beta
          Features:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Are provided "as-is" and may contain bugs or errors</li>
          <li>May be modified or discontinued at any time without notice</li>
          <li>May not be available to all users</li>
          <li>Are not guaranteed to become permanent features</li>
          <li>May have different terms or limitations</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">16.2 Feedback on Beta Features</h3>
        <p>By using Beta Features, you agree that:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Any feedback you provide may be used by TellerWand without compensation</li>
          <li>TellerWand may incorporate your feedback into the Service</li>
          <li>You grant TellerWand rights to use, modify, and commercialize your feedback</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">
          17. Intellectual Property Infringement Claims
        </h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">17.1 Reporting Infringement</h3>
        <p>
          If you believe your intellectual property rights have been infringed by content on the
          Service, please contact us immediately at support@tellerwand.com with the following
          information:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            A detailed description of the copyrighted work, trademark, or other intellectual
            property that you claim has been infringed
          </li>
          <li>
            The exact location (URL, page, section) of the allegedly infringing material on the
            Service
          </li>
          <li>
            Your contact information, including name, address, telephone number, and email address
          </li>
          <li>
            A statement that you have a good faith belief that the disputed use is not authorized by
            the intellectual property owner, its agent, or the law
          </li>
          <li>
            A statement, made under penalty of perjury, that the information in your notice is
            accurate and that you are the intellectual property owner or authorized to act on the
            owner's behalf
          </li>
          <li>Your physical or electronic signature</li>
        </ul>
        <p>
          <strong>False Claims:</strong> You acknowledge that making false claims of infringement
          may result in liability for damages, including costs and attorney's fees. TellerWand may
          pursue legal action against anyone who submits false or fraudulent infringement claims.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">17.2 TellerWand's Rights and Procedures</h3>
        <p>TellerWand reserves the right to:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            Remove or disable access to allegedly infringing content immediately upon receipt of a
            valid notice
          </li>
          <li>Terminate accounts of repeat infringers in appropriate circumstances</li>
          <li>
            Take appropriate legal action when necessary, including reporting to law enforcement
          </li>
          <li>Share your infringement notice with the alleged infringer</li>
          <li>Counter-notify if we believe the claim is invalid</li>
        </ul>
        <p>
          <strong>Counter-Notification:</strong> If content is removed due to an infringement claim,
          the user who posted the content may submit a counter-notification. TellerWand will review
          counter-notifications and may restore content if appropriate.
        </p>
        <p>
          <strong>No Liability:</strong> TellerWand is not liable for any damages resulting from
          removal of content in response to infringement claims, whether valid or invalid.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">
          18. Geographic Restrictions and Export Compliance
        </h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">18.1 Service Availability</h3>
        <p>
          The Service is intended for users in India and other jurisdictions where TellerWand
          operates. Access from restricted jurisdictions may be limited or prohibited.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">18.2 Export Compliance</h3>
        <p>
          You agree to comply with all applicable export control laws and regulations. You represent
          that:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>You are not located in a country subject to trade sanctions</li>
          <li>You are not on any restricted persons list</li>
          <li>You will not export or re-export the Service in violation of applicable laws</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">19. Mobile Applications</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">19.1 App Store Terms</h3>
        <p>If you download our mobile application from an app store:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Your use is also subject to the app store's terms and conditions</li>
          <li>TellerWand is not responsible for app store policies or practices</li>
          <li>App store may charge fees for downloads or in-app purchases</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">
          19.2 Mobile Device Requirements and Security
        </h3>
        <p>
          <strong>Your Responsibility for Device Security:</strong> You are solely responsible for:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Ensuring your device meets minimum system requirements</li>
          <li>Installing updates to maintain compatibility and security</li>
          <li>
            Securing your mobile device with appropriate passwords, biometrics, or other security
            measures
          </li>
          <li>
            Protecting your device from unauthorized access, malware, viruses, or security breaches
          </li>
          <li>Any charges from your mobile carrier or internet provider</li>
          <li>Maintaining the physical security of your device</li>
        </ul>
        <p>
          <strong>Device Security and Data Breaches:</strong> TellerWand is not responsible for:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>
            Any security breaches, data loss, or unauthorized access resulting from your failure to
            secure your device
          </li>
          <li>Loss of data or account access due to device theft, loss, or compromise</li>
          <li>Malware, viruses, or security vulnerabilities on your device</li>
          <li>Unauthorized access to your account through your compromised device</li>
          <li>
            Any damages resulting from your device being lost, stolen, or accessed by unauthorized
            persons
          </li>
        </ul>
        <p>
          <strong>Best Practices:</strong> You agree to:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Use strong passwords or biometric authentication on your device</li>
          <li>Keep your device's operating system and security software updated</li>
          <li>Not jailbreak, root, or otherwise compromise your device's security</li>
          <li>Log out of the Service when using shared or public devices</li>
          <li>Report lost or stolen devices immediately to TellerWand and your mobile carrier</li>
          <li>Enable remote wipe capabilities if available</li>
        </ul>
        <p>
          <strong>No Liability for Device-Related Issues:</strong> TellerWand disclaims all
          liability for any loss, damage, or unauthorized access resulting from:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Your failure to secure your mobile device</li>
          <li>Security vulnerabilities in your device's operating system or apps</li>
          <li>Malware, viruses, or malicious software on your device</li>
          <li>Physical theft or loss of your device</li>
          <li>Unauthorized access to your device by third parties</li>
        </ul>
        <p>
          <strong>Data on Your Device:</strong> Any data stored locally on your device is your
          responsibility. TellerWand recommends regularly syncing data to our servers and
          maintaining backups.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">20. Compliance with Local Laws</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">20.1 Your Compliance Obligations</h3>
        <p>You are responsible for:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Complying with all applicable local, state, and national laws</li>
          <li>Ensuring your use of the Service is legal in your jurisdiction</li>
          <li>Obtaining necessary licenses or permissions for your use</li>
          <li>Complying with tax obligations related to your use of the Service</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">20.2 TellerWand's Compliance</h3>
        <p>
          TellerWand operates in compliance with applicable Indian laws and regulations. We reserve
          the right to:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Refuse service to users in restricted jurisdictions</li>
          <li>Comply with legal requests from law enforcement</li>
          <li>Modify the Service to comply with legal requirements</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">21. Electronic Communications</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">
          21.1 Consent to Electronic Communications
        </h3>
        <p>
          By using the Service, you consent to receive communications from TellerWand
          electronically, including:
        </p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Email notifications</li>
          <li>In-app messages</li>
          <li>Push notifications (if enabled)</li>
          <li>Updates to these Terms or Privacy Policy</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">21.2 Withdrawal of Consent</h3>
        <p>
          You may withdraw consent for certain communications by adjusting your notification
          settings, but you cannot opt out of essential service-related communications.
        </p>

        <h2 className="text-xl font-semibold mt-6 mb-3">22. Miscellaneous</h2>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.1 Entire Agreement</h3>
        <p>
          These Terms, together with the Privacy Policy and any other legal notices published on the
          Service, constitute the entire agreement between you and TellerWand regarding the Service.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.2 Severability</h3>
        <p>
          If any provision of these Terms is found to be unenforceable or invalid, that provision
          shall be limited or eliminated to the minimum extent necessary, and the remaining
          provisions shall remain in full force and effect.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.3 Waiver</h3>
        <p>
          No waiver of any term of these Terms shall be deemed a further or continuing waiver of
          such term or any other term.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.4 No Waiver of Rights</h3>
        <p>
          TellerWand's failure to enforce any right or provision of these Terms shall not constitute
          a waiver of such right or provision. Any waiver must be in writing and signed by an
          authorized representative of TellerWand.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.5 Assignment</h3>
        <p>
          You may not assign or transfer these Terms or your account without TellerWand's prior
          written consent. TellerWand may assign these Terms without restriction.
        </p>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.6 Survival</h3>
        <p>The following sections shall survive termination of these Terms or your account:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Section 6 (Intellectual Property)</li>
          <li>Section 11 (Limitation of Liability)</li>
          <li>Section 12 (Indemnification)</li>
          <li>Section 13 (Dispute Resolution)</li>
          <li>Section 22 (Miscellaneous)</li>
          <li>Any other provisions that by their nature should survive</li>
        </ul>

        <h3 className="text-lg font-semibold mt-4 mb-2">22.7 Contact Information</h3>
        <p>For questions about these Terms, please contact us at:</p>
        <ul className="list-disc pl-6 mt-2 mb-4 space-y-1">
          <li>Email: support@tellerwand.com</li>
          <li>Website: https://tellerwand.com/contact-us</li>
        </ul>

        <h2 className="text-xl font-semibold mt-6 mb-3">23. Acceptance</h2>
        <p>
          By using TellerWand's services, you acknowledge that you have read, understood, and agree
          to be bound by these Terms of Service.
        </p>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-xs md:text-sm text-gray-600">
            <strong>TellerWand Inc.</strong>
            <br />
            Last Updated: January 5, 2026
            <br />
            Version: 1.0
          </p>
        </div>
      </CardContent>
    </Card>
  );

  if (user) {
    return (
      <RootLayout>
        <Helmet>
          <title>Terms of Service | TellerWand</title>
          <meta
            name="description"
            content="Read TellerWand's Terms of Service. Understand your rights and responsibilities when using our financial management platform."
          />
        </Helmet>
        <div className="container max-w-4xl mx-auto p-4 sm:p-6">
          <Button variant="outline" asChild className="mb-4 md:mb-6">
            <Link to="/dashboard" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Dashboard</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </Button>

          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 md:mb-6">
            Terms of Service
          </h1>

          {termsContent}
        </div>
      </RootLayout>
    );
  }

  return (
    <RootLayout>
      <Helmet>
        <title>Terms of Service | TellerWand</title>
        <meta
          name="description"
          content="Read TellerWand's Terms of Service. Understand your rights and responsibilities when using our financial management platform."
        />
      </Helmet>
      <div className="bg-gradient-to-b from-white to-gray-50">
        <div className="container max-w-4xl mx-auto p-4 sm:p-6">
          <Button variant="outline" asChild className="mb-4 md:mb-6">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Home</span>
              <span className="sm:hidden">Back</span>
            </Link>
          </Button>

          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 md:mb-6">
            Terms of Service
          </h1>

          {termsContent}
        </div>
      </div>
    </RootLayout>
  );
}
